import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Bugsnag from "@bugsnag/js";
import { Session } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { UserData } from "@/types/user-data";
import { AuthContext, AuthContextType } from "./AuthContext";
import { OAUTH_PROVIDER_SCOPES, type OAuthProviderId } from "@/config/oauth.config";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useAnalytics } from "@/lib/posthog";
import { applyDevPersonaToUserData, clearDevPersonaOverride } from "@/lib/devPersonaOverride";
import { clearUserScopedLocalData } from "@/lib/userScopedStorage";
import {
  inviteAcceptanceService,
  ApplyPendingInviteResult,
  isInviteAuthorizationDenied,
} from "@/services/inviteAcceptanceService";
import {
  buildUsersInsertRow,
  formatSupabaseProfileError,
  isUniqueViolation,
  missingColumnFromPostgrestSchemaCache,
  sanitizeOnboardingPayload,
  stripUndefined,
} from "@/lib/profileInsert";
import { syncAccountOrgLinkage } from "@/services/accountOrgRequestService";
import { getAuthBypassState } from "@/lib/authBypass";
import { normalizeEmailIdentity } from "@/lib/emailIdentity";
import { readOAuthErrorFromHash } from "@/lib/oauthRedirectError";
import { requiresProviderLinkConfirmation } from "@/lib/oauthLinkConfirmation";
import { userService } from "@/services/userService";

function reportUnexpectedAuthError(error: unknown, context: string): void {
  Bugsnag.notify(error instanceof Error ? error : new Error(String(error)), {
    context,
    severity: "warning" as const,
  });
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { identifyUser, reset } = useAnalytics();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  /** Row from `users` — source of truth for writes; dev persona overlay applied only for `userData` exposed on context. */
  const [storedUserData, setStoredUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [devPersonaOverrideTick, setDevPersonaOverrideTick] = useState(0);
  const [inviteApplication, setInviteApplication] = useState<ApplyPendingInviteResult | null>(null);
  const [policyAcceptedThisSession, setPolicyAcceptedThisSession] = useState(false);

  const supabaseSessionRef = useRef<Session | null | undefined>(undefined);
  /** Prevent duplicate auth SDK events from authorizing the same browser session twice. */
  const authorizedSessionRef = useRef<string | null>(null);
  const bypassInitializedRef = useRef(false);

  const userData = useMemo(
    () => applyDevPersonaToUserData(storedUserData),
    [storedUserData, devPersonaOverrideTick]
  );

  const bumpDevPersonaPreview = useCallback(() => {
    setDevPersonaOverrideTick((n) => n + 1);
  }, []);

  const markPolicyAcceptedThisSession = useCallback(() => setPolicyAcceptedThisSession(true), []);

  const loadUserProfile = async (userId: string): Promise<UserData | null> => {
    const { data, error } = await supabase.from("users").select("*").eq("user_id", userId).maybeSingle();
    if (error) throw error;
    const userData = data as UserData | null;
    setStoredUserData(userData);
    return userData;
  };

  const denyInviteSession = useCallback(
    async (userId: string) => {
      clearUserScopedLocalData(userId);
      reset();
      supabaseSessionRef.current = null;
      setSession(null);
      setIsAuthenticated(false);
      setStoredUserData(null);
      await supabase.auth.signOut();
      navigate("/login", { replace: true });
    },
    [navigate, reset]
  );

  const resetSessionState = useCallback(() => {
    authorizedSessionRef.current = null;
    supabaseSessionRef.current = null;
    setSession(null);
    setIsAuthenticated(false);
    setStoredUserData(null);
    setPolicyAcceptedThisSession(false);
    reset();
  }, [reset]);

  const activateSession = useCallback(
    async (nextSession: Session | null) => {
      if (!nextSession) {
        resetSessionState();
        setLoading(false);
        return;
      }

      const sessionKey = nextSession.access_token || nextSession.user.id;
      if (authorizedSessionRef.current === sessionKey) return;
      authorizedSessionRef.current = sessionKey;

      // AE-206: the first time a provider identity is linked onto an account
      // that already existed, confirm mailbox control with an email code before
      // letting the session into the app. Runs before the invite branch so an
      // unconfirmed link never applies an invite. Clearing the dedup ref keeps
      // the follow-up OTP session from being mistaken for a duplicate event.
      const linkConfirmation = requiresProviderLinkConfirmation(nextSession.user);
      if (linkConfirmation) {
        resetSessionState();
        // Local scope only: this is an advisory UX confirmation on this device,
        // not a security response, so it must not revoke the user's sessions
        // on their other devices too.
        await supabase.auth.signOut({ scope: "local" });
        setLoading(false);
        navigate("/login", {
          replace: true,
          state: {
            confirmProvider: linkConfirmation.provider,
            confirmProviderEmail: linkConfirmation.email,
          },
        });
        return;
      }

      const inviteId = new URLSearchParams(window.location.search).get("invite_id");
      if (inviteId) {
        try {
          const result = await inviteAcceptanceService.applyPendingInvite(inviteId);
          setInviteApplication(result);
          if (isInviteAuthorizationDenied(result)) {
            await denyInviteSession(nextSession.user.id);
            return;
          }
          if (result.reason === "conflict_existing_scope") {
            toast({
              title: "Invite not applied",
              description:
                "You're already assigned to a school in this workspace, so the invite was left pending for an admin to review.",
            });
          }
        } catch (error) {
          Bugsnag.notify(error instanceof Error ? error : new Error(String(error)), {
            severity: "warning",
            context: "auth.validateInvite",
          });
          await denyInviteSession(nextSession.user.id);
          return;
        }
      }

      try {
        if (!storedUserData) {
          await loadUserProfile(nextSession.user.id);
        }
      } catch (error) {
        reportUnexpectedAuthError(error, "loading user profile");
        await denyInviteSession(nextSession.user.id);
        return;
      }

      supabaseSessionRef.current = nextSession;
      setSession(nextSession);
      setIsAuthenticated(true);
      identifyUser(nextSession.user.id);

      const lastPath = localStorage.getItem("lastPath");
      if (lastPath) {
        localStorage.removeItem("lastPath");
        navigate(lastPath);
      }

      setLoading(false);
    },
    [denyInviteSession, identifyUser, navigate, resetSessionState, storedUserData, toast]
  );

  useEffect(() => {
    const bypassState = getAuthBypassState();
    if (bypassState) {
      if (!bypassInitializedRef.current) {
        bypassInitializedRef.current = true;
        supabaseSessionRef.current = bypassState.session;
        setSession(bypassState.session);
        setIsAuthenticated(true);
        setStoredUserData(bypassState.userData);
        setLoading(false);
      }
      return;
    }

    void supabase.auth.getSession().then(({ data: { session: initialSession } }) => activateSession(initialSession));

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_e, nextSession: Session | null) => {
      void activateSession(nextSession);
    });

    return () => subscription.unsubscribe();
  }, [activateSession]);

  /**
   * A failed provider redirect returns `#error=…` on the landing URL instead of
   * rejecting anything. Read it once at the root — the fragment does not
   * survive `ProtectedRoute`'s redirect to `/login` — then clear it so a
   * refresh does not repeat the toast.
   */
  useEffect(() => {
    const message = readOAuthErrorFromHash(window.location.hash);
    if (!message) return;
    toast({ title: "Sign-in failed", description: message, variant: "destructive" });
    // Pass the existing history state through rather than {}: AuthProvider is
    // mounted inside BrowserRouter, which keeps its own state ({usr, key, idx})
    // on the current entry, and wiping it degrades subsequent navigation and
    // scroll restoration. AE-206 fix 5.
    window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
  }, [toast]);

  const signInWithMagicLink = useCallback(async (email: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const identity = normalizeEmailIdentity(email);

    const { error } = await supabase.auth.signInWithOtp({
      email: identity,
      options: { emailRedirectTo: redirectUrl },
    });

    return { error };
  }, []);

  /**
   * Hands the browser to the provider. The client runs the implicit flow
   * (auth-js default, which the magic-link path already relies on), so the
   * session arrives back in the URL fragment and `detectSessionInUrl` turns it
   * into a session — no dedicated callback route is needed.
   *
   * No `prompt` query param on purpose: forcing an account chooser would break
   * the one-click requirement for users already signed in to the provider.
   */
  const signInWithOAuth = useCallback(async (provider: OAuthProviderId) => {
    const scopes = OAUTH_PROVIDER_SCOPES[provider];
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}/`, ...(scopes ? { scopes } : {}) },
    });
    return { error };
  }, []);

  const verifyOtp = useCallback(async (email: string, token: string) => {
    const { data, error } = await supabase.auth.verifyOtp({
      email: normalizeEmailIdentity(email),
      token,
      type: "email",
    });
    return { error, data };
  }, []);

  const signOut = useCallback(async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    clearUserScopedLocalData(user?.id);
    reset();
    if (import.meta.env.DEV) clearDevPersonaOverride();
    await supabase.auth.signOut();
  }, [reset]);

  const createUserProfile = useCallback(
    async (profileData: Omit<UserData, "created_at">) => {
      const insertRow = buildUsersInsertRow(profileData);
      const onboarding = profileData.onboarding;

      try {
        let payload: Record<string, unknown> = stripUndefined({ ...insertRow } as Record<string, unknown>);
        const droppedFromInsert: string[] = [];
        let data: UserData | null = null;

        for (let attempt = 0; attempt < 12; attempt++) {
          const { data: row, error } = await supabase.from("users").insert(payload).select().single();

          if (!error) {
            data = row as UserData;
            break;
          }

          if (isUniqueViolation(error)) {
            await loadUserProfile(profileData.user_id);
            const profilePatch = stripUndefined({
              ...(profileData.workspace_persona !== undefined && {
                workspace_persona: profileData.workspace_persona,
              }),
              ...(profileData.district_name !== undefined && {
                district_name: profileData.district_name,
              }),
              ...(profileData.custom_district_name !== undefined && {
                custom_district_name: profileData.custom_district_name,
              }),
              ...(profileData.school_name !== undefined && {
                school_name: profileData.school_name,
              }),
              ...(profileData.custom_school_name !== undefined && {
                custom_school_name: profileData.custom_school_name,
              }),
              ...(onboarding &&
                Object.keys(onboarding).length > 0 && {
                  onboarding: sanitizeOnboardingPayload(onboarding),
                }),
            });
            if (Object.keys(profilePatch).length > 0) {
              const { data: updated, error: upErr } = await supabase
                .from("users")
                .update(profilePatch)
                .eq("user_id", profileData.user_id)
                .select()
                .single();
              if (!upErr && updated) {
                setStoredUserData(updated);
              } else if (upErr) {
                reportUnexpectedAuthError(upErr, "updating an existing user profile");
              }
            }
            // Best-effort org FK link + Other admin alert (never blocks profile success).
            await syncAccountOrgLinkage(profileData);
            return;
          }

          const missing = missingColumnFromPostgrestSchemaCache(error);
          if (missing && Object.prototype.hasOwnProperty.call(payload, missing)) {
            droppedFromInsert.push(missing);
            const { [missing]: _removed, ...rest } = payload;
            payload = rest;
            continue;
          }

          throw error;
        }

        if (!data) {
          throw new Error("Profile insert failed after retries.");
        }

        let row = data;
        for (const key of droppedFromInsert) {
          const val = (profileData as Record<string, unknown>)[key];
          if (val === undefined) continue;
          const patch = { [key]: val } as Record<string, unknown>;
          const { data: updated, error: upErr } = await supabase
            .from("users")
            .update(patch)
            .eq("user_id", row.user_id)
            .select()
            .single();
          if (!upErr && updated) {
            row = updated as UserData;
          } else if (upErr) {
            reportUnexpectedAuthError(upErr, `saving user profile field ${key}`);
          }
        }

        if (onboarding && Object.keys(onboarding).length > 0) {
          const cleaned = sanitizeOnboardingPayload(onboarding);
          const { data: updated, error: upErr } = await supabase
            .from("users")
            .update({ onboarding: cleaned })
            .eq("user_id", row.user_id)
            .select()
            .single();

          if (!upErr && updated) {
            row = updated as UserData;
          } else if (upErr) {
            reportUnexpectedAuthError(upErr, "updating user onboarding data");
            row = { ...row, onboarding: cleaned };
          }
        }

        setStoredUserData(row);
        // Best-effort org FK link + Other admin alert (never blocks profile success).
        await syncAccountOrgLinkage(profileData);
      } catch (error) {
        reportUnexpectedAuthError(error, "creating user profile");
        toast({
          title: "Could not save profile",
          description: formatSupabaseProfileError(error),
          variant: "destructive",
        });
      }
    },
    [toast]
  );

  const updateUserSurvey = useCallback(
    async (surveyData: UserData["survey"]) => {
      if (!storedUserData) return;

      try {
        const { data, error } = await supabase
          .from("users")
          .update({ survey: surveyData, survey_completed_at: new Date().toISOString() })
          .eq("user_id", storedUserData.user_id)
          .select()
          .single();

        if (error) throw error;
        setStoredUserData(data);
      } catch (error) {
        reportUnexpectedAuthError(error, "updating user survey");
      }
    },
    [storedUserData]
  );

  /** AE-207: the profile screen presents any failure, so this deliberately does not catch. */
  const updateUserName = useCallback(
    async (name: { firstName: string; lastName?: string | null }) => {
      if (!storedUserData) return;

      const updated = await userService.updateUserName({
        userId: storedUserData.user_id,
        firstName: name.firstName,
        lastName: name.lastName,
      });
      setStoredUserData(updated);
    },
    [storedUserData]
  );

  const value: AuthContextType = {
    isAuthenticated,
    session,
    loading,
    userData,
    inviteApplication,
    bumpDevPersonaPreview: import.meta.env.DEV ? bumpDevPersonaPreview : undefined,

    updateUserSurvey,
    updateUserName,
    fillUserData: createUserProfile,
    signInWithMagicLink,
    signInWithOAuth,
    verifyOtp,
    signOut,
    policyAcceptedThisSession,
    markPolicyAcceptedThisSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
